import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export interface ArticleSection {
  heading: string;
  paragraphs: string[];
  list?: string[];
  numbered?: string[];
}

export interface PillarArticle {
  slug: string;
  title: string;
  tag: string;
  readTime: number;
  date: string;
  image: string;
  intro: string;
  sections: ArticleSection[];
  faqs: { q: string; a: string }[];
  related: { slug: string; label: string }[];
}

export default function ArticlePage({ article }: { article: PillarArticle }) {
  const { t } = useTranslation('home');
  const [activeToc, setActiveToc] = useState('section-0');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollTo = (id: string) => {
    setActiveToc(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="container-main pt-28 md:pt-32 pb-4">
        <nav className="flex items-center gap-2 text-sm text-foreground-400">
          <a href="/" className="hover:text-primary-600 transition-colors font-medium">Home</a>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-foreground-600">{article.title}</span>
        </nav>
      </div>

      <div className="container-main pb-16">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="lg:w-64 lg:flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Table of Contents</p>
              <ul className="space-y-1">
                {article.sections.map((section, i) => (
                  <li key={i}>
                    <button
                      onClick={() => scrollTo(`section-${i}`)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        activeToc === `section-${i}`
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {section.heading}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-6 p-4 rounded-lg bg-accent-50 border border-accent-200/50">
                <p className="text-xs font-medium text-accent-800 mb-2">Quick Links</p>
                <a href="/" className="block text-sm text-accent-600 hover:text-accent-700 mb-1">← Back to Home</a>
                {article.related.slice(0, 3).map((r) => (
                  <a key={r.slug} href={`/${r.slug}`} className="block text-sm text-accent-600 hover:text-accent-700 mb-1">
                    {r.label}
                  </a>
                ))}
              </div>

              <div className="mt-4 p-4 rounded-lg bg-primary-50 border border-primary-200/50">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary w-full text-sm justify-center"
                >
                  {t('cta.main_button')}
                </a>
              </div>
            </div>
          </aside>

          {/* Article Content */}
          <article className="flex-1 min-w-0">
            <header className="mb-8">
              <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700 mb-3">
                {article.tag}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  Updated {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {article.readTime} min read
                </span>
              </div>
            </header>

            <div className="prose prose-sm max-w-none">
              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>

              <p className="text-base text-foreground-700 leading-relaxed mb-6">{article.intro}</p>

              {article.sections.map((section, i) => (
                <div key={i}>
                  <h2 id={`section-${i}`} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
                  ))}
                  {section.numbered && (
                    <div className="bg-background-100 rounded-lg p-4 md:p-6 mb-6 border border-background-200/70">
                      <ol className="space-y-3 text-sm text-foreground-700">
                        {section.numbered.map((item, k) => (
                          <li key={k} className="flex items-start gap-3">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex-shrink-0">
                              {k + 1}
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {section.list && (
                    <ul className="space-y-2 mb-6 text-sm text-foreground-700">
                      {section.list.map((item, k) => (
                        <li key={k} className="flex items-start gap-2">
                          <i className="ri-check-line text-accent-500 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* FAQ */}
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4 mb-8">
                {article.faqs.map((faq, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-4 border border-background-200/50">
                    <p className="font-medium text-sm text-foreground-900 mb-2">{faq.q}</p>
                    <p className="text-sm text-foreground-600">{faq.a}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="bg-primary-50 rounded-lg p-6 md:p-8 border border-primary-200/50 text-center mt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Ready to Start Shopping?</h3>
                <p className="text-sm text-foreground-600 mb-4 max-w-md mx-auto">
                  Visit the main AllChinaBuy Spreadsheet website to browse products, compare sellers, and find your next purchase.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary inline-flex"
                >
                  {t('cta.visit_main')}
                  <i className="ri-external-link-line ml-1" />
                </a>
              </div>

              {/* Internal links */}
              <div className="mt-10 pt-6 border-t border-background-200/50">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Related Articles</p>
                <div className="flex flex-wrap gap-2">
                  <a href="/" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">
                    Home
                  </a>
                  {article.related.map((r) => (
                    <a key={r.slug} href={`/${r.slug}`} className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">
                      {r.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}